import React, { useState } from 'react';
import { X, Calendar, Clock, Plus, CheckCircle2, AlertCircle } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
import { ExerciseProgram, ProgramDay, WeeklyRoutineTask } from '../types';
import { loadAppState, saveAppState } from '../lib/storage';
import { savePlannerTask, mapWeeklyRoutineTaskToPlannerTask } from '../lib/plannerClient';

interface ScheduleProgramDayModalProps {
  isOpen: boolean;
  program: ExerciseProgram;
  day: ProgramDay;
  onClose: () => void;
  onScheduled: () => void;
}

export default function ScheduleProgramDayModal({
  isOpen,
  program,
  day,
  onClose,
  onScheduled
}: ScheduleProgramDayModalProps) {
  const { t } = useLanguage();
  const [dayOfWeek, setDayOfWeek] = useState<number>(1); // 1 = Monday / دوشنبه
  const [time, setTime] = useState<string>('08:00');
  const [endTime, setEndTime] = useState<string>('08:30');
  const [reminderEnabled, setReminderEnabled] = useState<boolean>(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  if (!isOpen) return null;

  const WEEKDAYS = [
    { value: 1, label: t("schedMon", "Monday (Mon)") },
    { value: 2, label: t("schedTue", "Tuesday (Tue)") },
    { value: 3, label: t("schedWed", "Wednesday (Wed)") },
    { value: 4, label: t("schedThu", "Thursday (Thu)") },
    { value: 5, label: t("schedFri", "Friday (Fri)") },
    { value: 6, label: t("schedSat", "Saturday (Sat)") },
    { value: 0, label: t("schedSun", "Sunday (Sun)") }
  ];

  const handleSchedule = async () => {
    setErrorMsg(null);
    setSuccessMsg(null);

    if (!time) {
      setErrorMsg(t("timeRequired", "Start time is required."));
      return;
    }

    const state = loadAppState();
    const currentRoutine = state.settings.weeklyRoutine || [];

    const newTask: WeeklyRoutineTask = {
      id: `task_prog_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      dayOfWeek: Number(dayOfWeek),
      time: time.trim(),
      endTime: endTime.trim() || undefined,
      title: `${program.title} — ${day.title}`,
      description: t("schedulesRehabDescriptionPattern", "Physiotherapy & Spine Recovery Exercises ({{count}} moves)", { count: day.exercises.length }),
      category: 'rehab',
      reminderEnabled,
      phoneNotificationEnabled: true,
      programId: program.id,
      programDayId: day.id
    };

    const updatedRoutine = [...currentRoutine, newTask];
    state.settings.weeklyRoutine = updatedRoutine;
    saveAppState(state, true);

    try {
      await savePlannerTask(mapWeeklyRoutineTaskToPlannerTask(newTask));
    } catch (e) {
      console.warn('Failed to sync scheduled task with D1 planner:', e);
    }

    setSuccessMsg(t("scheduledSuccessfully", "Workout scheduled successfully in weekly planner."));
    setTimeout(() => {
      onScheduled();
      onClose();
    }, 800);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-indigo-200 dark:border-indigo-900/40 w-full max-w-md overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/40 dark:to-purple-950/40">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-600 text-white rounded-xl shadow-md">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                {t("scheduleDayToPlanner", "Schedule Day to Weekly Planner")}
              </h3>
              <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                {program.title}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {errorMsg && (
            <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800/60 rounded-xl flex items-center gap-2.5 text-red-700 dark:text-red-300 text-xs">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <div>{errorMsg}</div>
            </div>
          )}

          {successMsg && (
            <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 rounded-xl flex items-center gap-2.5 text-emerald-700 dark:text-emerald-300 text-xs">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <div>{successMsg}</div>
            </div>
          )}

          <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
            <p className="text-xs font-bold text-gray-800 dark:text-gray-200">
              {day.title}
            </p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
              {t("dayExerciseCountInfo", "Exercises in this day: {{count}}", { count: day.exercises.length })}
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              {t("selectWeekday", "Select Weekday:")}
            </label>
            <select
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(Number(e.target.value))}
              className="w-full p-2.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500"
            >
              {WEEKDAYS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                {t("startTime", "Start Time:")}
              </label>
              <div className="relative">
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="w-full p-2.5 pr-8 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500"
                />
                <Clock className="w-4 h-4 text-gray-400 absolute right-2.5 top-3" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                {t("endTime", "End Time (optional):")}
              </label>
              <div className="relative">
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full p-2.5 pr-8 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500"
                />
                <Clock className="w-4 h-4 text-gray-400 absolute right-2.5 top-3" />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id="reminderCheck"
              checked={reminderEnabled}
              onChange={(e) => setReminderEnabled(e.target.checked)}
              className="w-4 h-4 text-indigo-600 rounded-xs border-gray-300 focus:ring-indigo-500"
            />
            <label htmlFor="reminderCheck" className="text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
              {t("enableReminder", "Enable Reminder & Alarm")}
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors cursor-pointer"
          >
            {t("cancel", "Cancel")}
          </button>
          <button
            type="button"
            onClick={handleSchedule}
            className="px-4 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-md transition-all flex items-center gap-1.5 cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>{t("scheduleConfirmBtn", "Schedule Workout")}</span>
          </button>
        </div>

      </div>
    </div>
  );
}
